import React, { useEffect, useCallback } from 'react';
import { useGetSessionId } from 'src/components/kiosk-pro/kiosk-pro-shared/use-session-id';
import { DUPLICATE_PHONE_SIGN_IN_MESSAGE } from 'src/components/kiosk-pro/kiosk-pro-shared/duplicate-phone-message';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import ArrowLeft from 'src/assets/arrow-left';

import {
  PhoneNumberContainer,
  Title,
  PhoneNumberInputWrapper,
  PhoneNumberPadWrapper,
  PhoneNumberDisplay,
  PhoneNumberText,
  BlinkingCursor,
  ReadOnlyInput,
  PhoneNumberPadGrid,
  Number,
  ZeroButton,
  EnterButton,
  PhoneNumberPadContainer,
} from './phone-number-pad.styles';
import { usePhoneNumberPad } from './use-phone-number-pad';
import { usePhoneNumberLogin } from './use-phone-number-login';
import { PhoneNumberLoginSuccess } from '../phone-number-login-success';

type PhoneNumberPadProps = {
  className?: string;
  isInModal?: boolean;
  onClose?: () => void;
  onLoginSuccess?: () => void;
  onResetLogin?: () => void;
  onDuplicatePhone?: () => void;
};

/**
 * Phone number pad component for kiosk login.
 * Allows users to enter their phone number to login.
 */
export function PhoneNumberPad({
  className,
  isInModal,
  onClose,
  onLoginSuccess,
  onResetLogin,
  onDuplicatePhone,
}: PhoneNumberPadProps): JSX.Element | null {
  const { dispensary } = useDispensary();
  const cName = String(dispensary?.cName ?? '');
  const kioskMenuUrl = `/kiosks/${cName}`;

  const {
    phoneNumber,
    inputRef,
    handleNumberClick,
    handleInputChange,
    showCursor,
    formattedPhone,
    digits,
  } = usePhoneNumberPad();
  const sessionId = useGetSessionId();
  const {
    handlePhoneLogin,
    resetLoginState,
    isLoading,
    isSuccess,
    isError,
    isDuplicatePhone,
    loginState,
    userFirstName,
  } = usePhoneNumberLogin(sessionId);

  const handleEnterClick = useCallback(async (): Promise<void> => {
    if (digits.length !== 10 || isLoading) {
      return;
    }

    await handlePhoneLogin(phoneNumber);
  }, [digits.length, isLoading, handlePhoneLogin, phoneNumber]);

  useEffect(() => {
    if (isSuccess && onLoginSuccess) {
      onLoginSuccess();
    }
  }, [isSuccess, onLoginSuccess]);

  useEffect(() => {
    if (isDuplicatePhone) {
      onDuplicatePhone?.();
    }
  }, [isDuplicatePhone, onDuplicatePhone]);

  // Ensure input receives focus and stays focused
  useEffect(() => {
    if (inputRef.current && !isLoading && !isSuccess) {
      inputRef.current.focus();
    }
  }, [inputRef, isLoading, isSuccess]);

  const handleContainerClick = useCallback((): void => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputRef]);

  // Keep input focused at all times
  useEffect(() => {
    if (isLoading || isSuccess) {
      return undefined;
    }

    const handleBlur = (): void => {
      // Refocus immediately when input loses focus
      if (inputRef.current) {
        // Use setTimeout to ensure blur completes before refocusing
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 0);
      }
    };

    const input = inputRef.current;
    if (!input) {
      return undefined;
    }

    input.addEventListener('blur', handleBlur);

    return () => {
      input.removeEventListener('blur', handleBlur);
    };
  }, [inputRef, isLoading, isSuccess]);

  const handleResetLoginState = (): void => {
    resetLoginState();
    onResetLogin?.();
  };

  if (isDuplicatePhone) {
    if (onDuplicatePhone) {
      return null;
    }

    return (
      <PhoneNumberContainer className={className} $isInModal={isInModal}>
        <Title $isInModal={isInModal}>{DUPLICATE_PHONE_SIGN_IN_MESSAGE}</Title>
      </PhoneNumberContainer>
    );
  }

  // Show success message
  if (isSuccess) {
    return (
      <PhoneNumberLoginSuccess
        className={className}
        kioskMenuUrl={kioskMenuUrl}
        onClose={onClose}
        onResetLoginState={handleResetLoginState}
        userFirstName={userFirstName}
        isInModal={isInModal}
      />
    );
  }

  return (
    <PhoneNumberContainer className={className} $isInModal={isInModal} onClick={handleContainerClick}>
      <Title $isInModal={isInModal}>
        {isError && loginState.errorMessage ? loginState.errorMessage : 'Enter phone number'}
      </Title>
      <PhoneNumberInputWrapper>
        <PhoneNumberDisplay>
          <PhoneNumberText $isInModal={isInModal}>{formattedPhone}</PhoneNumberText>
          {showCursor && <BlinkingCursor $isInModal={isInModal}>|</BlinkingCursor>}
        </PhoneNumberDisplay>
        <ReadOnlyInput
          ref={inputRef}
          type='tel'
          id='phone-number'
          autoFocus
          autoComplete='off'
          value={phoneNumber}
          onChange={handleInputChange}
          onKeyDown={(e) => {
            // Prevent tabbing away
            if (e.key === 'Tab') {
              e.preventDefault();
              return;
            }
            // Handle Enter key to submit
            if (e.key === 'Enter' && digits.length === 10 && !isLoading) {
              e.preventDefault();
              void handleEnterClick();
            }
          }}
          onClick={(e) => {
            // Ensure input stays focused when display is clicked
            e.currentTarget.focus();
          }}
        />
      </PhoneNumberInputWrapper>
      <PhoneNumberPadContainer $isInModal={isInModal}>
        <PhoneNumberPadWrapper>
          <PhoneNumberPadGrid>
            {Array.from({ length: 9 }, (_, i) => i + 1).map((number) => (
              <Number
                key={number}
                value={number}
                role='button'
                tabIndex={0}
                onClick={() => handleNumberClick(number)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleNumberClick(number);
                  }
                }}
              >
                {number}
              </Number>
            ))}
            <ZeroButton
              value={0}
              role='button'
              tabIndex={0}
              onClick={() => handleNumberClick(0)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleNumberClick(0);
                }
              }}
            >
              0
            </ZeroButton>
            <Number
              value='<-'
              role='button'
              tabIndex={0}
              onClick={() => handleNumberClick('<-')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleNumberClick('<-');
                }
              }}
            >
              <ArrowLeft />
            </Number>
          </PhoneNumberPadGrid>
        </PhoneNumberPadWrapper>

        <EnterButton $isInModal={isInModal} disabled={digits.length !== 10 || isLoading} onClick={handleEnterClick}>
          {isLoading ? 'Signing in...' : 'Enter'}
        </EnterButton>
      </PhoneNumberPadContainer>
    </PhoneNumberContainer>
  );
}
